import { Directive, ElementRef, HostListener, Input, OnInit, Optional, Self } from '@angular/core';
import { NgControl } from '@angular/forms';

@Directive({
  selector: 'input[appInputLimit], textarea[appInputLimit]',
  standalone: true
})
export class InputLimitDirective implements OnInit {  // ← renamed to match import

  private _min: number | null = null;
  private _max: number | null = null;

  @Input() set min(value: number | string | null) {
    this._min = value !== null && value !== undefined ? Number(value) : null;
  }
  @Input() set max(value: number | string | null) {
    this._max = value !== null && value !== undefined ? Number(value) : null;
  }

  private isNumberInput = false;
  private _maxLengthResolved = false;
  private resolvedMaxLength: number | null = null;

  constructor(
    private el: ElementRef<HTMLInputElement | HTMLTextAreaElement>,
    @Optional() @Self() private ngControl: NgControl
  ) { }

  ngOnInit(): void {
    this.isNumberInput = this.el.nativeElement.type === 'number';
  }

  // ─── Lazy MaxLength Resolver ───────────────────────────────────────────────

  private getMaxLength(): number | null {
    if (!this._maxLengthResolved) {
      this._maxLengthResolved = true;
      this.resolvedMaxLength = this.extractMaxLength();
    }
    return this.resolvedMaxLength;
  }

  private extractMaxLength(): number | null {
    if (!this.ngControl?.control) return null;

    const control = this.ngControl.control;
    const probe = 'a'.repeat(99999);
    const errors = control.validator?.({ ...control, value: probe } as any);
    return errors?.['maxlength']?.requiredLength ?? null;
  }

  // ─── Keydown ───────────────────────────────────────────────────────────────

  @HostListener('keydown', ['$event'])
  onKeyDown(event: Event): void {
    const keyEvent = event as KeyboardEvent;

    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
      'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
    if (allowedKeys.includes(keyEvent.key) || keyEvent.ctrlKey || keyEvent.metaKey) return;

    if (this.isNumberInput) {
      this.handleNumberKeyDown(keyEvent);
    } else {
      this.handleTextKeyDown(keyEvent);
    }
  }

  // ─── Paste ─────────────────────────────────────────────────────────────────

  @HostListener('paste', ['$event'])
  onPaste(event: Event): void {
    // console.log('Paste event detected');
    const clipboardEvent = event as ClipboardEvent;
    clipboardEvent.preventDefault();
    const pasted = clipboardEvent.clipboardData?.getData('text') ?? '';

    if (this.isNumberInput) {
      this.handleNumberPaste(pasted);
    } else {
      this.handleTextPaste(pasted);
    }
  }

  // ─── Input (safety net) ────────────────────────────────────────────────────

  @HostListener('input')
  onInput(): void {
    if (this.isNumberInput) {
      this.clampNumberValue();
    } else {
      this.truncateTextValue();
    }
  }

  // ─── Number Handlers ───────────────────────────────────────────────────────

  private handleNumberKeyDown(event: KeyboardEvent): void {
    // Only block non-numeric keys — remove the peek-check entirely.
    // type="number" inputs return null for selectionStart/End so
    // the future-value calculation was building wrong strings like
    // "9" + "12.99" = "912.99" and incorrectly blocking valid input.
    // onInput() clamp is the reliable enforcement point instead.
    const allowedSpecial = ['.', '-'];
    if (allowedSpecial.includes(event.key)) return;

    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
      return;
    }

    // ↓ Block if already has 2 decimal places
    const input = this.el.nativeElement as HTMLInputElement;
    const currentValue = input.value;
    const dotIndex = currentValue.indexOf('.');
    if (dotIndex !== -1 && currentValue.length - dotIndex > 2) {
      event.preventDefault();
    }
  }

  private clampNumberValue(): void {
    const input = this.el.nativeElement as HTMLInputElement;
    if (isNaN(input.valueAsNumber)) return;

    let value = input.valueAsNumber;

    // ↓ Round to 2 decimal places
    value = Math.round(value * 100) / 100;

    const clamped = this.clamp(value);

    if (clamped !== input.valueAsNumber) {
      input.value = String(clamped);
      this.ngControl?.control?.setValue(clamped, { emitEvent: true });
    }
  }

  private handleNumberPaste(text: string): void {
    const value = parseFloat(text);
    if (!isNaN(value)) {
      // ↓ Round to 2 decimal places before clamping
      const rounded = Math.round(value * 100) / 100;
      const clamped = this.clamp(rounded);
      (this.el.nativeElement as HTMLInputElement).value = String(clamped);
      this.ngControl?.control?.setValue(clamped, { emitEvent: true });
    }
  }

  private clamp(value: number): number {
    let result = value;
    if (this._min !== null) result = Math.max(this._min, result);
    if (this._max !== null) result = Math.min(this._max, result);
    return result;
  }

  // ─── Text Handlers ─────────────────────────────────────────────────────────

  private handleTextKeyDown(event: KeyboardEvent): void {
    const maxLength = this.getMaxLength();
    if (maxLength === null) return;

    const input = this.el.nativeElement as HTMLInputElement;
    const selectionLength = (input.selectionEnd ?? 0) - (input.selectionStart ?? 0);
    const willGrow = selectionLength === 0;

    if (willGrow && input.value.length >= maxLength) {
      event.preventDefault();
    }
  }

  private handleTextPaste(text: string): void {
    const maxLength = this.getMaxLength();
    if (maxLength === null) {
      this.insertAtCursor(text);
      return;
    }
    const input = this.el.nativeElement as HTMLInputElement;
    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? 0;
    const currentWithoutSelection = input.value.slice(0, start) + input.value.slice(end);
    const remaining = maxLength - currentWithoutSelection.length;
    this.insertAtCursor(text.slice(0, Math.max(0, remaining)));
  }

  private truncateTextValue(): void {
    const maxLength = this.getMaxLength();
    if (maxLength === null) return;

    const input = this.el.nativeElement as HTMLInputElement;
    if (input.value.length > maxLength) {
      input.value = input.value.slice(0, maxLength);
      this.dispatchInputEvent();
    }
  }

  // ─── Helpers ───────────────────────────────────────────────────────────────

  private insertAtCursor(text: string): void {
    const input = this.el.nativeElement as HTMLInputElement;

    // selectionStart/selectionEnd throw InvalidStateError for type="email",
    // type="number", type="date", etc. per the HTML spec. Fall back to
    // appending at the end of the current value in that case.
    let start: number;
    let end: number;
    try {
      start = input.selectionStart ?? input.value.length;
      end = input.selectionEnd ?? input.value.length;
    } catch {
      start = input.value.length;
      end = input.value.length;
    }

    input.value = input.value.slice(0, start) + text + input.value.slice(end);

    try {
      input.selectionStart = input.selectionEnd = start + text.length;
    } catch {
      // cursor repositioning not supported for this input type — safe to ignore
    }

    this.ngControl?.control?.setValue(input.value, { emitEvent: true });
    this.dispatchInputEvent();
  }

  private dispatchInputEvent(): void {
    this.el.nativeElement.dispatchEvent(new Event('input'));
  }
}